import { useCallback } from 'react';
import ToastMsg from '../components/common/ToastMsg';
import { useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { clearAuth } from '../redux/slices/authSlice';
import {clearUserInfo} from '../redux/slices/userSlice';
import { persistor } from '../redux/store';
 
export const useLogout = () => {
  const dispatch = useDispatch();
  const { logout: auth0Logout } = useAuth0();
 
  const logout = useCallback(async (options?: { returnTo?: string }) => {
    try {
      sessionStorage.setItem('is_logging_out', 'true');

      // Clear Redux auth state
      dispatch(clearAuth());
      dispatch(clearUserInfo());

      // Purge persisted state and wait for it to complete
      await persistor.purge();

      // Clear Auth0 specific cache keys from localStorage BEFORE general clear
      // This ensures Auth0 doesn't have any session data to restore
      const auth0CacheKeys = Object.keys(localStorage).filter(key => 
        key.startsWith('@@auth0spajs') || 
        key.includes('auth0') || 
        key.includes('access_token') || 
        key.includes('refresh_token')
      );
      auth0CacheKeys.forEach(key => localStorage.removeItem(key));

      // Clear local/session storage completely
      localStorage.clear();
      sessionStorage.clear();

      // Re-set the logout flag after clearing session storage
      sessionStorage.setItem('is_logging_out', 'true');

      // Prepare logout redirect URL
      let returnTo = options?.returnTo || process.env.REACT_APP_AUTH0_LOGOUT_RETURN_URL || '/login';
      if (returnTo && !returnTo.startsWith('http')) {
        returnTo = `${window.location.origin}${returnTo.startsWith('/') ? '' : '/'}${returnTo}`;
      }

      // Use Auth0's logout method to clear their session server-side
      // This is more reliable than calling the logout endpoint directly
      const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN;
      const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
      
      if (auth0Domain && clientId) {
        // Call Auth0 logout endpoint with proper parameters
        const logoutUrl = `https://${auth0Domain}/v2/logout?client_id=${clientId}&returnTo=${encodeURIComponent(returnTo)}`;
        // Use a very short delay to ensure all state is written before navigation
        window.location.replace(logoutUrl);
      } else {
        // Fallback to SPA logout if env vars are missing
        auth0Logout({
          logoutParams: {
            returnTo
          },
        });
      }
      return;
    } catch (error: any) {
      console.error('Error during logout:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
      });
      // Clear the logout flag on error
      sessionStorage.removeItem('is_logging_out');
      ToastMsg('error', 'Logout failed. Please try again.');
      console.warn('Logout process failed - some cleanup may be incomplete');
      throw error;
    }
  }, [dispatch, auth0Logout]);
  return { logout };
};